import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowLeft, FiGithub, FiExternalLink, FiCalendar, FiUser, FiDownload } from "react-icons/fi";
import { FaApple } from "react-icons/fa";

const projectsData = {
  1: {
    id: 1,
    title: "Boxing RingBell Timer (iOS)",
    fullDescription: "A specialized iOS timer application designed specifically for boxing training sessions. The app allows boxers and trainers to create custom workout timers with configurable round durations, rest intervals, and preparation times. Features include audio alerts, visual countdown indicators, and the ability to save custom timer configurations for different training routines.",
    image: "https://images.unsplash.com/photo-1549576490-b0b4831ef60a",
    tags: ["Swift", "iOS", "Timer", "Audio", "User Interface"],
    features: [
      "Custom round and rest interval timers",
      "Audio alerts for round start/end",
      "Visual countdown indicators",
      "Save custom timer configurations",
      "Background operation support",
      "Clean, intuitive interface"
    ],
    technologies: {
      "Frontend": ["Swift", "UIKit", "Core Audio"],
      "Features": ["Timer Management", "Audio Alerts", "Local Storage"],
      "Platform": ["iOS 14+", "iPhone", "iPad"]
    },
    screenshots: [
      "/assets/boxingTimer1.png",
      "/assets/BoxingTimer2.png",
      "/assets/BoxingTimer3.png",
      "/assets/BoxingTimer4.png"
    ],
    appStoreLink: "#",
    isIOSApp: true,
    status: "In Development",
    duration: "3 months"
  },
  2: {
    id: 2,
    title: "Plant Identification App (iOS)",
    fullDescription: "A free, ad-supported iOS plant identification app built with SwiftUI that competes with premium apps like PictureThis by offering unlimited plant scans without subscription fees. The app leverages AI-powered identification to recognize 400,000+ plant species, provides comprehensive care guides, personal plant collection management with smart care reminders, and photo-based growth tracking.",
    image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b",
    tags: ["Swift", "SwiftUI", "iOS", "Core Data", "AI/ML", "Plant.id API"],
    features: [
      "AI-powered identification of 400,000+ plant species",
      "Unlimited free plant scans (ad-supported)",
      "Comprehensive care guides with watering schedules",
      "Personal plant collection with health tracking",
      "Smart care reminders with local notifications",
      "Growth timeline with photo logging",
      "Camera and photo library integration",
      "Offline-capable Core Data storage",
      "Dark mode support"
    ],
    technologies: {
      "Platform": ["iOS 16.0+", "SwiftUI", "UIKit"],
      "Architecture": ["MVVM Pattern", "Combine Framework"],
      "Storage": ["Core Data", "Local Persistence"],
      "AI/ML": ["Plant.id API", "Pl@ntNet API", "Vision Framework", "Core ML"],
      "APIs": ["Perenual Care API", "Google AdMob"],
      "Features": ["Camera/AVFoundation", "Local Notifications", "Image Processing"]
    },
    screenshots: [
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b",
      "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae",
      "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735"
    ],
    appStoreLink: "#",
    isIOSApp: true,
    status: "In Development",
    duration: "4 months"
  },
  3: {
    id: 3,
    title: "Financial Dashboard",
    fullDescription: "A comprehensive financial dashboard designed for showcasing company earnings and performance metrics by product categories. Features interactive charts, real-time data visualization, and detailed analytics to help businesses understand their revenue streams and make data-driven decisions.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
    tags: ["React", "Chart.js", "D3.js", "Financial APIs", "Data Visualization"],
    features: [
      "Interactive revenue charts and graphs",
      "Product-wise earnings breakdown",
      "Real-time financial data updates",
      "Customizable dashboard widgets",
      "Export reports in multiple formats",
      "Responsive design for all devices"
    ],
    technologies: {
      "Frontend": ["React", "TypeScript", "Tailwind CSS"],
      "Data Viz": ["Chart.js", "D3.js", "Recharts"],
      "Backend": ["Node.js", "Express", "PostgreSQL"],
      "APIs": ["Financial APIs", "Real-time WebSockets"]
    },
    screenshots: [
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3"
    ],
    github: "#",
    liveLink: "#",
    status: "Completed",
    duration: "5 months"
  },
  4: {
    id: 4,
    title: "WordPress Websites",
    fullDescription: "A collection of custom WordPress websites developed for various clients, featuring responsive designs, e-commerce integration, and content management systems. Each site is tailored to specific business needs with custom themes, plugins, and optimized performance.",
    image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d",
    tags: ["WordPress", "PHP", "MySQL", "Custom Themes", "WooCommerce"],
    features: [
      "Custom WordPress theme development",
      "E-commerce integration with WooCommerce",
      "Responsive and mobile-first design",
      "SEO optimization and performance tuning",
      "Custom plugin development",
      "Content management training for clients"
    ],
    technologies: {
      "CMS": ["WordPress", "Custom Themes", "Plugin Development"],
      "Frontend": ["HTML5", "CSS3", "JavaScript", "SCSS"],
      "Backend": ["PHP", "MySQL", "REST APIs"],
      "E-commerce": ["WooCommerce", "Payment Gateways", "Inventory Management"]
    },
    screenshots: [
      "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d",
      "https://images.unsplash.com/photo-1558655146-9f40138edfeb",
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    ],
    github: "#",
    liveLink: "#",
    status: "Multiple Projects",
    duration: "Ongoing"
  },
  5: {
    id: 5,
    title: "Three.js 3D Viewer",
    fullDescription: "An advanced interactive 3D model viewer application built with Three.js and WebGL. The viewer supports various 3D file formats, real-time lighting manipulation, material editing, and smooth camera controls. Designed for showcasing 3D models, product visualizations, and interactive presentations with high-performance rendering.",
    image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe",
    tags: ["Three.js", "WebGL", "JavaScript", "3D Graphics", "Interactive"],
    features: [
      "Support for multiple 3D file formats (GLB, GLTF, OBJ)",
      "Real-time lighting and shadow controls",
      "Interactive camera controls (orbit, pan, zoom)",
      "Material and texture customization",
      "Animation playback and timeline controls",
      "Export and screenshot functionality",
      "Performance optimization for large models",
      "Responsive design for all devices"
    ],
    technologies: {
      "3D Graphics": ["Three.js", "WebGL", "GLSL Shaders"],
      "Frontend": ["JavaScript", "HTML5 Canvas", "CSS3"],
      "File Handling": ["GLB/GLTF Loader", "OBJ Loader", "Texture Loading"],
      "Controls": ["Orbit Controls", "Transform Controls", "GUI Controls"],
      "Performance": ["LOD System", "Frustum Culling", "Texture Compression"]
    },
    screenshots: [
      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe",
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee",
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13"
    ],
    github: "#",
    liveLink: "#",
    status: "Completed",
    duration: "6 months"
  }
};

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [activeScreenshot, setActiveScreenshot] = useState(0);

  useEffect(() => {
    const projectData = projectsData[parseInt(id)];
    if (projectData) {
      setProject(projectData);
    }
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
          <button onClick={() => navigate('/')} className="btn-primary">
            Back to Portfolio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-light">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg shadow-soft">
        <div className="container flex justify-between items-center py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-neutral-dark hover:text-primary transition-colors"
          >
            <FiArrowLeft className="mr-2" />
            Back to Portfolio
          </button>
          <a href="/" className="font-heading text-2xl font-bold gradient-text">aivApps</a>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <div className="container">
          {/* Project Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
              {project.title}
            </h1>
            <p className="text-xl text-neutral-medium max-w-3xl mx-auto mb-6">
              {project.fullDescription}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center text-neutral-medium">
                <FiCalendar className="mr-2" />
                Duration: {project.duration}
              </div>
              <div className="flex items-center text-neutral-medium">
                <FiUser className="mr-2" />
                Status: {project.status}
              </div>
            </div>
            <div className="flex justify-center gap-4">
              {project.isIOSApp ? (
                <a
                  href={project.appStoreLink}
                  className="btn-primary inline-flex items-center bg-black hover:bg-gray-800 text-white px-8 py-3 rounded-xl transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaApple className="mr-2 text-xl" />
                  Download on the App Store
                </a>
              ) : (
                <>
                  <a href={project.github} className="btn-outline">
                    <FiGithub className="mr-2" />
                    View Code
                  </a>
                  <a href={project.liveLink} className="btn-primary">
                    <FiExternalLink className="mr-2" />
                    Live Demo
                  </a>
                </>
              )}
            </div>
          </motion.div>

          {/* Screenshots Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Screenshots</h2>
            <div className={project.isIOSApp ? "max-w-md mx-auto" : "max-w-4xl mx-auto"}>
              <div className="rounded-2xl overflow-hidden shadow-xl mb-6 bg-gray-100 flex items-center justify-center">
                <img
                  src={project.screenshots[activeScreenshot]}
                  alt={`${project.title} screenshot ${activeScreenshot + 1}`}
                  className={project.isIOSApp ? "w-full h-auto object-contain max-h-[600px]" : "w-full h-96 object-cover"}
                />
              </div>
              <div className="flex justify-center gap-4 flex-wrap">
                {project.screenshots.map((screenshot, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveScreenshot(index)}
                    className={`${project.isIOSApp ? 'w-16 h-28' : 'w-20 h-20'} rounded-lg overflow-hidden border-2 transition-colors ${activeScreenshot === index ? 'border-primary' : 'border-gray-200'
                      }`}
                  >
                    <img
                      src={screenshot}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Project Details Grid */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="glass-card p-8"
            >
              <h3 className="text-2xl font-bold mb-6">Key Features</h3>
              <ul className="space-y-3">
                {project.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-neutral-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Technologies */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="glass-card p-8"
            >
              <h3 className="text-2xl font-bold mb-6">Technologies Used</h3>
              <div className="space-y-6">
                {Object.entries(project.technologies).map(([category, techs]) => (
                  <div key={category}>
                    <h4 className="font-semibold text-primary mb-2">{category}</h4>
                    <div className="flex flex-wrap gap-2">
                      {techs.map((tech, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-full"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Back to Projects */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center"
          >
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              <FiArrowLeft className="mr-2" />
              Back to All Projects
            </button>
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default ProjectDetail;